/**
 * WizardStepProjectType (Development Project Step 2)
 *
 * Selects the type of development project:
 * - Residential Subdivision
 * - Townhome Community
 * - Apartment Complex
 * - Mixed-Use Development
 * - Commercial Complex
 */

import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, Layers, Home, Building2, FolderKanban } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WizardProjectData } from '@/types/wizard';
import type { DevelopmentProjectType } from '@/types/project';

interface WizardStepProjectTypeProps {
  data: WizardProjectData;
  onUpdate: (data: Partial<WizardProjectData>) => void;
}

interface ProjectTypeOption {
  type: DevelopmentProjectType;
  name: string;
  description: string;
  icon: React.ReactNode;
  unitLabel: string;
}

const PROJECT_TYPES: ProjectTypeOption[] = [
  {
    type: 'subdivision',
    name: 'Residential Subdivision',
    description: 'Single-family home lots with shared infrastructure',
    icon: <Layers className="h-6 w-6" />,
    unitLabel: 'Lot',
  },
  {
    type: 'townhome',
    name: 'Townhome Community',
    description: 'Attached single-family homes sharing common walls',
    icon: <Home className="h-6 w-6" />,
    unitLabel: 'Townhome',
  },
  {
    type: 'apartment',
    name: 'Apartment Complex',
    description: 'Multi-family residential buildings',
    icon: <Building2 className="h-6 w-6" />,
    unitLabel: 'Building',
  },
  {
    type: 'mixed_use',
    name: 'Mixed-Use Development',
    description: 'Residential and commercial in one project',
    icon: <FolderKanban className="h-6 w-6" />,
    unitLabel: 'Unit',
  },
  {
    type: 'commercial_complex',
    name: 'Commercial Complex',
    description: 'Multi-building commercial development',
    icon: <Building2 className="h-6 w-6" />,
    unitLabel: 'Building',
  },
];

export function WizardStepProjectType({ data, onUpdate }: WizardStepProjectTypeProps) {
  const handleSelectType = (type: DevelopmentProjectType) => {
    // Find the project type to get its default unit label
    const projectType = PROJECT_TYPES.find((pt) => pt.type === type);

    onUpdate({
      developmentType: type,
      unitLabelPrefix: projectType?.unitLabel || 'Unit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Question */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold">What type of development?</h2>
        <p className="text-muted-foreground">
          Select the type that best describes your project
        </p>
      </div>

      {/* Options */}
      <div className="grid gap-3">
        {PROJECT_TYPES.map((option) => {
          const isSelected = data.developmentType === option.type;

          return (
            <Card
              key={option.type}
              className={cn(
                'relative cursor-pointer transition-all hover:shadow-sm',
                isSelected
                  ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                  : 'border-border hover:border-primary/50'
              )}
              onClick={() => handleSelectType(option.type)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div
                    className={cn(
                      'p-2.5 rounded-lg',
                      isSelected
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {option.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-medium">{option.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {option.description}
                    </p>
                  </div>

                  {/* Selected indicator */}
                  {isSelected && (
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Help text */}
      <p className="text-center text-sm text-muted-foreground">
        This helps us determine the right permit structure for your project.
      </p>
    </div>
  );
}
